import React, { FC } from 'react';

import MediaUploaderWrapper from 'application/components/MediaUploaderWrapper';
import { TEXTS } from 'application/pages/AdsPage/renderer/texts';
import { ACCEPT_AUDIO_FILETYPES, ACCEPT_VIDEO_FILETYPES } from 'application/pages/constants';
import CommonUtils from 'application/utils/CommonUtils';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { withProviders } from 'application/helpers';
import PlayerProvider from 'application/providers/PlayerProvider';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import Input, { InputType } from '@zvuk-b2b/react-uikit/ui/Input';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';

import VideoMediaUploaderWrapper from 'application/components/VideoMediaUploaderWrapper';
import { AdCampaignMediaTypeEnum } from '@shared/api/graphql/generated';
import useAdCreateModalPresenter from './useAdCreateModalPresenter';
import useAdCreateModalController from './useAdCreateModalController';

import styles from './index.module.scss';

const MAX_AD_NAME_LENGTH = 138;
const MAX_VALID_NAME_LENGTH = MAX_AD_NAME_LENGTH - 10; // -10 because of error message symbol limit

const AdCreateModal: FC<ModalProps[ModalTypes.AD_CAMPAIGN_CREATE]> = ({
  close,
  onCreate,
  adCampaignMediaType = AdCampaignMediaTypeEnum.Audio,
}) => {
  const { isLoading, createAdCampaign, clearAdCampaigns } = useAdCreateModalPresenter();
  const {
    items,
    adName,
    setAdName,
    isAnyItemLoading,
    onFilesSelected,
    onRetryFile,
    onSubmitClick,
    onCancelClick,
    onRemoveClick,
  } = useAdCreateModalController({
    close,
    onCreate,
    createAdCampaign,
    clearAdCampaigns,
    adCampaignMediaType,
  });

  const modalTitle =
    adCampaignMediaType === AdCampaignMediaTypeEnum.Audio
      ? TEXTS.MODAL_AUDIO_AD_CREATE_TITLE
      : TEXTS.MODAL_VIDEO_AD_CREATE_TITLE;

  const inputError = adName?.length >= MAX_VALID_NAME_LENGTH;
  const onlyWhitespace = adName?.trim().length === 0;

  const uploadButtonText =
    adCampaignMediaType === AdCampaignMediaTypeEnum.Audio
      ? TEXTS.INPUT_AD_CREATE_MEDIA_UPLOAD_TITLE
      : TEXTS.INPUT_AD_CREATE_VIDEO_MEDIA_UPLOAD_TITLE;

  return (
    <ModalScreen
      title={modalTitle}
      titlePlacement="left"
      onClose={onCancelClick}
      actionButtons={[
        <Button
          key="button_create_new_ad_modal"
          elementId="button_create_new_ad_modal"
          type={ButtonType.PRIMARY}
          isDisabled={
            adName.length === 0 ||
            items.length === 0 ||
            !!items.find((item) => !!item.error) ||
            isAnyItemLoading ||
            isLoading ||
            adName.length >= MAX_VALID_NAME_LENGTH ||
            onlyWhitespace
          }
          onClick={onSubmitClick}
          isLoading={isLoading}>
          {TEXTS.BUTTON_AD_CREATE_TITLE}
        </Button>,
      ]}>
      <div className={styles.AdCreateModalInput}>
        <Input
          elementId="input_start-campaign"
          placeholder={TEXTS.INPUT_AD_CREATE_PLACEHOLDER}
          label={TEXTS.INPUT_AD_CREATE_TITLE}
          isDisabled={isLoading}
          type={InputType.TEXT}
          value={adName}
          onChange={setAdName}
          maxLength={MAX_AD_NAME_LENGTH}
          forceError={inputError}
          containerClassName={styles.input}
          labelClassName={styles.label}
        />
        <span className={`${styles.helperText} ${inputError ? styles.errorText : ''}`}>
          {TEXTS.INPUT_AD_CREATE_ERROR}
        </span>
      </div>

      {adCampaignMediaType === AdCampaignMediaTypeEnum.Audio && (
        <MediaUploaderWrapper
          elementId="media_uploader_create_new_campaign"
          accept={ACCEPT_AUDIO_FILETYPES}
          items={items}
          isLoading={isLoading}
          buttonTitle={TEXTS.INPUT_AD_CREATE_MEDIA_UPLOAD_TITLE}
          description={TEXTS.MODAL_AD_CREATE_MEDIA_UPLOAD_DESC(
            CommonUtils.convertObjectValuesToString(ACCEPT_AUDIO_FILETYPES)
          )}
          onDrop={onFilesSelected}
          onRemove={onRemoveClick}
          onRetry={onRetryFile}
          mediaUploaderClassname={styles.AdCreateModalMediaUploaderList}
        />
      )}

      {adCampaignMediaType === AdCampaignMediaTypeEnum.Video && (
        <VideoMediaUploaderWrapper
          elementId="media_uploader_create_new_campaign"
          accept={ACCEPT_VIDEO_FILETYPES}
          items={items}
          isLoading={isLoading}
          buttonTitle={uploadButtonText}
          description={TEXTS.VIDEO_UPLOAD_FORMAT(CommonUtils.convertObjectValuesToString(ACCEPT_VIDEO_FILETYPES))}
          onDrop={onFilesSelected}
          onRemove={onRemoveClick}
          onRetry={onRetryFile}
          mediaUploaderClassname={styles.AdCreateModalMediaUploaderList}
        />
      )}
    </ModalScreen>
  );
};

export default withProviders(AdCreateModal, [PlayerProvider]);
